import { create } from 'zustand';
import { getNetworkOperator } from '../../wonder-navigation/api';
import {
  allPackages,
  frogfootPackages,
  openservePackages,
  octotelPackages,
  vumatelPackages,
  metrofibrePackages,
  seacomPackages,
  centuryCityPackages,
  lightwirePackages,
  ttConnectPackages,
  openFibrePackages,
  fttbPackageIds
} from '../../../pages/PackagesPage/suppliers';

/**
 * Comparison Store
 * 
 * A Zustand store that manages the state for the comparison modals.
 * This centralizes all modal state and logic to ensure consistent behavior
 * regardless of where the modals are triggered from.
 */
const useComparisonStore = create((set, get) => ({
  // Modal visibility state - COMPLETELY SEPARATED
  // Selection and Comparison modals share one state system
  isSelectionModalOpen: false,
  isComparisonModalOpen: false,
  
  // Recommendation modal has its own independent state system
  isRecommendationModalOpen: false,
  
  // Selection state
  selectedOperators: [],
  recommendedSpeed: null,
  priceRange: [0, 2000],
  speedRange: [0, 1000],
  sortBy: 'price',
  sortDirection: 'asc',
  
  // Package state
  availableOperators: [],
  packages: [],
  filteredPackages: [],
  selectedPackages: [],
  
  // Recommendation state - completely isolated
  recommendationAnswers: {
    usageTypes: [],     // Array of selected usage types (max 2): streaming, gaming, work, general
    usageIntensity: '', // light, moderate, heavy
    devices: 0,         // number of connected devices
    users: 0,           // number of users
    activities: []      // array of activities (streaming, gaming, video calls, etc.)
  },
  
  // Initialize available operators
  initializeAvailableOperators: () => {
    // Create a map of all unique operators from the packages
    const operatorsMap = new Map();
    
    // Process all packages to find unique operators
    allPackages.forEach(pkg => {
      const supplier = pkg.Supplier;
      let operatorName = supplier;
      
      // Determine connection type based on package ID
      const connectionType = fttbPackageIds.includes(pkg.ID) ? 'FTTB' : 'FTTH';
      
      // Normalize Vumatel variants to just "Vumatel"
      if (supplier.toLowerCase().includes('vumatel') || supplier.toLowerCase().includes('vuma')) {
        operatorName = 'Vumatel';
      }
      
      // Create a unique key for this operator+connection type
      const key = `${operatorName}:${connectionType}`;
      
      // Only add if we haven't seen this combination before
      if (!operatorsMap.has(key)) {
        // Create a display name that includes connection type for business packages
        const displayName = connectionType === 'FTTB' ?
          `${operatorName} Business` : operatorName;
        
        operatorsMap.set(key, {
          operatorName: operatorName,
          displayName: displayName,
          connectionType: connectionType,
          logo: getNetworkOperator(operatorName)?.logo
        });
      }
    });
    
    set({ availableOperators: Array.from(operatorsMap.values()) });
  },
  
  // Actions for opening/closing modals
  openSelectionModal: (params = {}) => {
    // Check if the modal is already open to prevent unnecessary updates
    const { isSelectionModalOpen, availableOperators } = get();
    if (isSelectionModalOpen) return;
    
    // Check if recommendation modal was just opened
    const recommendationJustOpened = localStorage.getItem('recommendationJustOpened') === 'true';
    if (recommendationJustOpened) {
      console.log('Skipping openSelectionModal because recommendation modal was just opened');
      return;
    }
    
    // Initialize available operators if not already done
    if (availableOperators.length === 0) {
      get().initializeAvailableOperators();
    }
    
    // Only set the selection modal to open without affecting other modals
    set({
      isSelectionModalOpen: true
    });
    
    // Handle optional parameters
    if (params.selectAllOperators) {
      // Use setTimeout to break the synchronous chain of state updates
      setTimeout(() => {
        get().selectAllOperators();
      }, 0);
    } else if (params.name) {
      // Select a specific operator by name
      setTimeout(() => {
        get().selectOperator(params.name);
      }, 0);
    }
    
    // Log for debugging
    console.log('Selection modal opened with params:', params);
  },
  
  openComparisonModal: () => {
    const { selectedOperators, isComparisonModalOpen, isSelectionModalOpen } = get();
    
    // Check if the modal is already open to prevent unnecessary updates
    if (isComparisonModalOpen) return;
    
    // Only open comparison modal if selection modal is open and operators are selected
    if (isSelectionModalOpen && selectedOperators.length > 0) {
      set({
        isComparisonModalOpen: true
      });
      
      console.log('Comparison modal opened with operators:', selectedOperators);
    } else {
      console.warn('Cannot open comparison modal: Selection modal not open or no operators selected');
    }
  },
  
  openRecommendationModal: () => {
    // Check if the modal is already open to prevent unnecessary updates
    const { isRecommendationModalOpen } = get();
    if (isRecommendationModalOpen) {
      console.log('Recommendation modal already open, ignoring openRecommendationModal call');
      return;
    }
    
    console.log('Starting openRecommendationModal process');
    
    // Clear any existing localStorage flags that might interfere with modal behavior
    localStorage.removeItem('comingFromRecommendation');
    console.log('Cleared comingFromRecommendation flag');
    
    // Set a flag to indicate the recommendation modal was just opened
    // This helps prevent the selection modal from opening immediately after
    localStorage.setItem('recommendationJustOpened', 'true');
    console.log('Set recommendationJustOpened flag to true');
    
    // Log the current modal states before changing
    const { isSelectionModalOpen, isComparisonModalOpen } = get();
    console.log('Modal states before opening recommendation:', {
      isRecommendationModalOpen,
      isSelectionModalOpen,
      isComparisonModalOpen
    });
    
    // IMPORTANT: Only modify the recommendation modal state
    // DO NOT touch any other modal states to ensure complete isolation
    set({
      isRecommendationModalOpen: true,
      // Reset recommendation answers to ensure a fresh start
      recommendationAnswers: {
        usageTypes: [],
        usageIntensity: '',
        devices: 0,
        users: 0,
        activities: []
      }
    });
    
    console.log('Recommendation modal opened - COMPLETELY ISOLATED');
    
    // Log the current modal states after changing
    const updatedState = get();
    console.log('Modal states after opening recommendation:', {
      isRecommendationModalOpen: updatedState.isRecommendationModalOpen,
      isSelectionModalOpen: updatedState.isSelectionModalOpen,
      isComparisonModalOpen: updatedState.isComparisonModalOpen
    });
    
    // Clear the flag after a delay to allow the UI to stabilize
    setTimeout(() => {
      localStorage.removeItem('recommendationJustOpened');
      console.log('Cleared recommendationJustOpened flag');
    }, 1000);
  },
  
  // Close ONLY the recommendation modal
  closeRecommendationModal: () => {
    set({
      isRecommendationModalOpen: false
    });
    
    console.log('Recommendation modal closed - ISOLATED');
  },
  
  closeAllModals: () => {
    // Log the current modal states before closing
    const { isSelectionModalOpen, isComparisonModalOpen, isRecommendationModalOpen } = get();
    console.log('Modal states before closeAllModals:', {
      isRecommendationModalOpen,
      isSelectionModalOpen,
      isComparisonModalOpen
    });
    
    // Get the stack trace to see where closeAllModals is being called from
    const stackTrace = new Error().stack;
    console.log('closeAllModals called from:', stackTrace);
    
    // Check if recommendation modal was just opened
    const recommendationJustOpened = localStorage.getItem('recommendationJustOpened') === 'true';
    
    // Check if selection modal is being opened
    const selectionModalOpening = localStorage.getItem('selectionModalOpening') === 'true';
    
    if (recommendationJustOpened && isRecommendationModalOpen) {
      console.log('Preventing closeAllModals from closing recommendation modal that was just opened');
      // Only close selection and comparison modals
      set({
        isSelectionModalOpen: false,
        isComparisonModalOpen: false
      });
    } else if (selectionModalOpening && isSelectionModalOpen) {
      console.log('Preventing closeAllModals from closing selection modal that is being opened');
      // Only close recommendation and comparison modals
      set({
        isRecommendationModalOpen: false,
        isComparisonModalOpen: false
      });
    } else {
      // Close all modals
      set({
        isSelectionModalOpen: false,
        isComparisonModalOpen: false,
        isRecommendationModalOpen: false
      });
    }
    
    console.log('Modals closed');
  },
  
  // Close only selection and comparison modals
  closeSelectionAndComparisonModals: () => {
    set({
      isSelectionModalOpen: false,
      isComparisonModalOpen: false
    });
    
    console.log('Selection and comparison modals closed');
  },
  
  // Actions for managing selections
  setSelectedOperators: (operators) => {
    set({ selectedOperators: operators });
    console.log('Selected operators updated:', operators);
    
    // Fetch packages when operators change
    get().fetchPackages();
  },
  
  selectOperator: (operatorName) => {
    const { availableOperators } = get();
    
    // Find the operator in available operators
    const operator = availableOperators.find(op => 
      op.operatorName.toLowerCase() === operatorName.toLowerCase()
    );
    
    if (operator) {
      const operatorIdentifier = `${operator.operatorName}:${operator.connectionType || 'FTTH'}`;
      set({ selectedOperators: [operatorIdentifier] });
      console.log(`Selected operator: ${operatorName}`);
      
      // Fetch packages when operator changes
      get().fetchPackages();
    } else {
      console.warn(`Operator not found: ${operatorName}`);
    }
  },
  
  selectAllOperators: () => {
    const { availableOperators } = get();
    
    // Create identifiers for all operators
    const allOperatorIds = availableOperators.map(op =>
      `${op.operatorName}:${op.connectionType}`
    );
    
    set({ selectedOperators: allOperatorIds });
    console.log('All operators selected');
    
    // Fetch packages when operators change
    get().fetchPackages();
  },
  
  // Fetch packages based on selected operators
  fetchPackages: () => {
    const { selectedOperators } = get();
    
    if (selectedOperators.length > 0) {
      console.log('Fetching packages for operators:', selectedOperators);
      const filteredPackages = [];
      
      selectedOperators.forEach(operatorIdentifier => {
        // Parse the operator identifier to get name and connection type
        const [operatorName, connectionType = 'FTTH'] = operatorIdentifier.split(':');
        console.log(`Processing operator: ${operatorName}, connectionType: ${connectionType}`);
        
        // Filter packages for this operator
        let operatorPackages = [];
        
        // Match packages based on the supplier name and connection type
        if (operatorName === 'Vumatel') {
          // For Vumatel, include all Vumatel packages but filter by connection type
          if (connectionType === 'FTTH') {
            // For FTTH, include regular Vumatel packages
            operatorPackages = vumatelPackages.filter(pkg => {
              const normalizedSupplier = pkg.Supplier.toLowerCase();
              const isVumatel = normalizedSupplier.includes('vumatel') || normalizedSupplier.includes('vuma');
              
              // Check if this is a FTTH package based on the ID
              const isFTTH = !fttbPackageIds.includes(pkg.ID);
              
              return isVumatel && isFTTH;
            });
          } else if (connectionType === 'FTTB') {
            // For FTTB, include Vumatel Tactical packages
            operatorPackages = vumatelPackages.filter(pkg => {
              const normalizedSupplier = pkg.Supplier.toLowerCase();
              const isVumatelTactical = normalizedSupplier.includes('tactical');
              
              // Check if this is a FTTB package based on the ID
              const isFTTB = fttbPackageIds.includes(pkg.ID);
              
              return isVumatelTactical || isFTTB;
            });
          }
        } else if (operatorName === 'Openserve') {
          // For Openserve, filter by connection type
          if (connectionType === 'FTTH') {
            // For FTTH, exclude packages with IDs in fttbPackageIds
            operatorPackages = openservePackages.filter(pkg => {
              return !fttbPackageIds.includes(pkg.ID);
            });
          } else if (connectionType === 'FTTB') {
            // For FTTB, include packages with IDs in fttbPackageIds
            operatorPackages = openservePackages.filter(pkg => {
              return fttbPackageIds.includes(pkg.ID);
            });
          }
        } else if (operatorName === 'Frogfoot') {
          // For Frogfoot, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = frogfootPackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = frogfootPackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'MetroFibre' || operatorName === 'Metrofibre' || operatorName === 'Metro Fibre') {
          // For MetroFibre, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = metrofibrePackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = metrofibrePackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'Octotel') {
          // For Octotel, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = octotelPackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = octotelPackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'Seacom') {
          // For Seacom, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = seacomPackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = seacomPackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'Century City' || operatorName === 'ThinkSpeed' || operatorName === 'Century City Connect') {
          // For Century City, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = centuryCityPackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = centuryCityPackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'Lightwire') {
          // For Lightwire, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = lightwirePackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = lightwirePackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'TTConnect' || operatorName === 'TT Connect') {
          // For TTConnect, filter by connection type
          if (connectionType === 'FTTH') {
            operatorPackages = ttConnectPackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
          } else if (connectionType === 'FTTB') {
            operatorPackages = ttConnectPackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        } else if (operatorName === 'OpenFibre' || operatorName === 'Open Fibre') {
          // For OpenFibre, filter by connection type
          if (connectionType === 'FTTH') {
            // Get all FTTH packages
            const allFtthPackages = openFibrePackages.filter(pkg => !fttbPackageIds.includes(pkg.ID));
            
            // Deduplicate packages based on download speed and price
            const uniquePackages = [];
            const seen = new Set();
            
            allFtthPackages.forEach(pkg => {
              const key = `${pkg["Download speed (kbps)"]}-${pkg["Upload speed (kbps)"]}-${pkg.Price}`;
              if (!seen.has(key)) {
                seen.add(key);
                uniquePackages.push(pkg);
              }
            });
            
            operatorPackages = uniquePackages;
          } else if (connectionType === 'FTTB') {
            operatorPackages = openFibrePackages.filter(pkg => fttbPackageIds.includes(pkg.ID));
          }
        }
        
        // Add operator name and connection type to each package
        const packagesWithOperator = operatorPackages.map(pkg => ({
          ...pkg,
          operatorName: operatorName,
          // Set the connectionType based on the package ID
          connectionType: fttbPackageIds.includes(pkg.ID) ? 'FTTB' : 'FTTH',
          operator: getNetworkOperator(operatorName)
        }));
        
        console.log(`Found ${operatorPackages.length} packages for ${operatorName} (${connectionType})`);
        filteredPackages.push(...packagesWithOperator);
      });
      
      console.log(`Total filtered packages: ${filteredPackages.length}`);
      set({ packages: filteredPackages });
      
      // Apply filters and sorting
      get().applyFiltersAndSorting();
    } else {
      set({ packages: [], filteredPackages: [] });
    }
  },
  
  // Apply filters and sorting
  applyFiltersAndSorting: () => {
    const { packages, speedRange, priceRange, sortBy, sortDirection, recommendedSpeed } = get();
    
    if (packages.length > 0) {
      let filtered = [...packages];
      
      // Apply speed filter
      filtered = filtered.filter(pkg => {
        const speedMbps = pkg["Download speed (kbps)"] / 1024;
        return speedMbps >= speedRange[0] && speedMbps <= speedRange[1];
      });
      
      // Apply price filter
      filtered = filtered.filter(pkg => {
        return pkg.Price >= priceRange[0] && pkg.Price <= priceRange[1];
      });
      
      // Apply sorting
      filtered.sort((a, b) => {
        if (sortBy === 'price') {
          return sortDirection === 'asc' ? a.Price - b.Price : b.Price - a.Price;
        } else if (sortBy === 'speed') {
          return sortDirection === 'asc'
            ? a["Download speed (kbps)"] - b["Download speed (kbps)"]
            : b["Download speed (kbps)"] - a["Download speed (kbps)"];
        } else if (sortBy === 'type') {
          // Sort by connection type (FTTH first, then FTTB) and then by symmetry
          const aType = a.connectionType || 'FTTH';
          const bType = b.connectionType || 'FTTH';
          
          // Check if connections are symmetrical
          const aSymmetrical = a["Download speed (kbps)"] === a["Upload speed (kbps)"];
          const bSymmetrical = b["Download speed (kbps)"] === b["Upload speed (kbps)"];
          
          // First sort by connection type
          if (aType !== bType) {
            return sortDirection === 'asc'
              ? aType.localeCompare(bType)
              : bType.localeCompare(aType);
          }
          
          // Then sort by symmetry
          if (aSymmetrical !== bSymmetrical) {
            return sortDirection === 'asc'
              ? (aSymmetrical ? -1 : 1)
              : (bSymmetrical ? -1 : 1);
          }
          
          // If both have same type and symmetry, sort by speed
          return sortDirection === 'asc'
            ? a["Download speed (kbps)"] - b["Download speed (kbps)"]
            : b["Download speed (kbps)"] - a["Download speed (kbps)"];
        }
        return 0;
      });
      
      set({ filteredPackages: filtered });
    } else {
      set({ filteredPackages: [] });
    }
  },
  
  // Actions for managing filters
  setPriceRange: (range) => {
    set({ priceRange: range });
    get().applyFiltersAndSorting();
  },
  
  setSpeedRange: (range) => {
    set({ speedRange: range });
    get().applyFiltersAndSorting();
  },
  
  setSortBy: (sortBy) => {
    set({ sortBy });
    get().applyFiltersAndSorting();
  },
  
  setSortDirection: (sortDirection) => {
    set({ sortDirection });
    get().applyFiltersAndSorting();
  },
  
  setRecommendedSpeed: (speed) => {
    set({ recommendedSpeed: speed });
    
    // Update speed filter based on recommendation
    if (speed) {
      set({ speedRange: [speed * 0.8, speed * 1.2] });
      get().applyFiltersAndSorting();
    }
  },
  
  resetFilters: () => {
    set({
      priceRange: [0, 2000],
      speedRange: [0, 1000],
      recommendedSpeed: null,
      sortBy: 'price',
      sortDirection: 'asc'
    });
    
    get().applyFiltersAndSorting();
    console.log('Filters reset');
  },
  
  // Actions for managing selected packages
  setSelectedPackages: (packages) => {
    set({ selectedPackages: packages });
  },
  
  clearSelectedPackages: () => {
    set({ selectedPackages: [] });
  },
  
  // Update recommendation answers
  updateRecommendationAnswers: (answers) => {
    set(state => ({
      recommendationAnswers: { ...state.recommendationAnswers, ...answers }
    }));
    
    // Calculate recommended speed if we have enough data
    const { recommendationAnswers } = get();
    if (recommendationAnswers.usageTypes && recommendationAnswers.usageTypes.length > 0) {
      get().calculateRecommendedSpeed();
    }
  },
  
  // Calculate recommended speed based on recommendation answers
  calculateRecommendedSpeed: () => {
    const { recommendationAnswers } = get();
    
    // Check if we have at least one usage type selected
    const usageTypes = recommendationAnswers.usageTypes || [];
    if (usageTypes.length > 0) {
      // Standard speed tiers - maintain the same tiers for compatibility
      const speedTiers = [10, 20, 25, 50, 100, 200, 500, 1000];
      
      // 1. ENHANCED BASE SPEED CALCULATION
      
      // Calculate base speed for each selected usage type
      const baseSpeeds = usageTypes.map(usageType => {
        switch (usageType) {
          case 'general':
            return 25; // Good for basic browsing
          case 'streaming':
            return 40; // Adjusted from 50 to be more realistic for standard HD
          case 'gaming':
            return 75; // Adjusted from 100 to be more realistic
          case 'work':
            return 50; // Adjusted from 75 to be more realistic
          default:
            return 25;
        }
      });
      
      // Use the maximum as the starting point
      let baseSpeed = Math.max(...baseSpeeds, 25); // Default to 25 if empty array
      
      // Add a bonus if multiple usage types are selected to account for diverse needs
      if (usageTypes.length > 1) {
        baseSpeed = Math.round(baseSpeed * 1.2); // 20% bonus for diverse needs
      }
      
      // Adjust base speed based on usage intensity if available
      if (recommendationAnswers.usageIntensity) {
        const intensityMultiplier =
          recommendationAnswers.usageIntensity === 'light' ? 0.8 :
          recommendationAnswers.usageIntensity === 'heavy' ? 1.2 : 1;
        baseSpeed = Math.round(baseSpeed * intensityMultiplier);
      }
      
      // 2. IMPROVED USER SCALING
      // More linear scaling that better accounts for multiple simultaneous users
      // 1 user: 1x, 2 users: 1.5x, 3 users: 2x, 4 users: 2.5x, etc.
      const users = Math.max(1, recommendationAnswers.users || 1);
      const userMultiplier = 1 + (users - 1) * 0.5;
      
      // 3. IMPROVED DEVICE SCALING
      // Better scaling for multiple devices with a reasonable cap
      const devices = Math.max(1, recommendationAnswers.devices || 1);
      const deviceMultiplier = Math.min(2.5, 1 + Math.log10(devices + 1));
      
      // 4. ENHANCED ACTIVITY BONUSES
      // Scale activity bonuses based on user count
      const activities = recommendationAnswers.activities || [];
      let activityBonus = 0;
      
      // User scaling factor for activities - more users means more potential simultaneous usage
      const userScalingFactor = Math.min(1.5, 0.7 + (users - 1) * 0.1);
      
      // Apply activity bonuses with improved values and scaling
      if (activities.includes('4k-streaming')) activityBonus += 25 * userScalingFactor;
      if (activities.includes('video-calls')) activityBonus += 15 * userScalingFactor;
      if (activities.includes('large-downloads')) activityBonus += 40 * userScalingFactor;
      if (activities.includes('cloud-backup')) activityBonus += 20 * userScalingFactor;
      if (activities.includes('file-sharing')) activityBonus += 15 * userScalingFactor;
      
      // 5. ACCOUNT FOR SIMULTANEOUS ACTIVITIES
      // More activities happening at once need more bandwidth
      const simultaneousFactor = Math.min(1.5, 1 + (activities.length - 1) * 0.15);
      if (activities.length > 1) {
        activityBonus *= simultaneousFactor;
      }
      
      // 6. CALCULATE RAW RECOMMENDED SPEED
      const calculatedSpeed = Math.round(baseSpeed * userMultiplier * deviceMultiplier + activityBonus);
      
      // 7. INTELLIGENT TIER SELECTION
      // Find the nearest standard speed tier with smarter selection logic
      
      // First find the highest tier that's less than or equal to calculated speed
      const lowerTier = [...speedTiers].filter(tier => tier <= calculatedSpeed).pop() || speedTiers[0];
      
      // Then find the lowest tier that's greater than calculated speed
      const higherTier = speedTiers.find(tier => tier >= calculatedSpeed) || speedTiers[speedTiers.length - 1];
      
      // Determine final tier with smart selection logic
      let recommendedTier;
      
      // If calculated speed is very close to a lower tier (within 15%), use that tier
      if (calculatedSpeed <= lowerTier * 1.15) {
        recommendedTier = lowerTier;
      }
      // If calculated speed is much closer to lower tier than higher tier, use lower tier
      else if (higherTier - calculatedSpeed > calculatedSpeed - lowerTier) {
        recommendedTier = lowerTier;
      }
      // Otherwise use the higher tier
      else {
        recommendedTier = higherTier;
      }
      
      // 8. SAFETY CHECK - ensure we return a valid tier
      // If something went wrong, default to a reasonable tier
      if (!speedTiers.includes(recommendedTier)) {
        // Find the closest tier as a fallback
        recommendedTier = speedTiers.reduce((prev, curr) =>
          Math.abs(curr - calculatedSpeed) < Math.abs(prev - calculatedSpeed) ? curr : prev
        );
      }
      
      // Set the recommended speed
      set({ recommendedSpeed: recommendedTier });
      
      // Update speed filter based on recommendation
      set({ speedRange: [recommendedTier * 0.8, recommendedTier * 1.2] });
      
      // Apply filters and sorting
      get().applyFiltersAndSorting();
      
      return recommendedTier;
    }
    
    return null;
  },
  
  // Handle recommendation completion - ISOLATED
  handleRecommendationComplete: (recommendedSpeed) => {
    // Only proceed if we have a valid recommended speed
    // This prevents the function from running prematurely
    if (recommendedSpeed) {
      console.log('Recommendation completed with speed:', recommendedSpeed);
      
      // Update the recommended speed without closing the modal
      // and without affecting other modals
      set({
        recommendedSpeed
      });
      
      // We don't automatically close the recommendation modal or open the selection modal
      // This allows the user to engage with the recommendation modal
      
    } else {
      console.log('Recommendation not completed yet, no speed provided');
    }
  },
  
  // Explicit function to manually transition from recommendation to selection
  // This should ONLY be called when the user explicitly clicks a button
  proceedToSelectionFromRecommendation: () => {
    const { recommendedSpeed } = get();
    
    console.log('User explicitly requested to proceed to selection with speed:', recommendedSpeed);
    
    // First select all operators BEFORE closing the recommendation modal
    // This ensures the operators are selected before any state changes
    get().selectAllOperators();
    
    // Verify operators are selected
    const selectedOperators = get().selectedOperators;
    console.log('Selected operators before transition:', selectedOperators);
    
    // Set a flag in localStorage to indicate we're coming from recommendation
    localStorage.setItem('comingFromRecommendation', 'true');
    
    // Close the recommendation modal
    set({
      isRecommendationModalOpen: false
    });
    
    // Add a delay to ensure UI updates properly
    setTimeout(() => {
      // Verify operators are still selected
      const currentOperators = get().selectedOperators;
      console.log('Selected operators during transition:', currentOperators);
      
      // If operators were somehow lost, select them again
      if (currentOperators.length === 0) {
        console.log('Operators were lost, selecting them again');
        get().selectAllOperators();
      }
      
      // Then open the selection modal with the operators already selected
      set({
        isSelectionModalOpen: true
      });
      
      console.log('Selection modal opened after recommendation with operators:', get().selectedOperators);
      
      // Add a short delay and then open the comparison modal to show results
      setTimeout(() => {
        // Open the comparison modal to show the results
        set({
          isComparisonModalOpen: true
        });
        
        console.log('Comparison modal opened to show recommendation results');
        
        // Clear the flag after the modals are fully open
        localStorage.removeItem('comingFromRecommendation');
        console.log('Cleared comingFromRecommendation flag');
      }, 500);
    }, 300);
  },
  
  // Utility functions
  formatSpeed: (speedKbps) => {
    if (!speedKbps && speedKbps !== 0) return '0 Mbps';
    const speedMbps = speedKbps / 1024;
    return speedMbps >= 1000 ? `${(speedMbps / 1000).toFixed(1)} Gbps` : `${speedMbps} Mbps`;
  },
  
  getUsageProfile: (pkg) => {
    if (!pkg || !pkg["Download speed (kbps)"] || !pkg["Upload speed (kbps)"]) return null;
    
    const downloadSpeed = pkg["Download speed (kbps)"] / 1024; // Convert to Mbps
    const uploadSpeed = pkg["Upload speed (kbps)"] / 1024; // Convert to Mbps
    
    const profiles = [];
    
    // Streaming profile
    if (downloadSpeed >= 100) {
      profiles.push('Great for Streaming');
    } else if (downloadSpeed >= 50) {
      profiles.push('Good for Streaming');
    }
    
    // Gaming profile
    if (downloadSpeed >= 50 && uploadSpeed >= 20) {
      profiles.push('Great for Gaming');
    }
    
    // Work from home profile
    if (uploadSpeed >= 20 && downloadSpeed >= 50) {
      profiles.push('Perfect for WFH');
    } else if (uploadSpeed >= 10 && downloadSpeed >= 25) {
      profiles.push('Good for WFH');
    }
    
    // If symmetrical connection
    if (downloadSpeed === uploadSpeed && downloadSpeed >= 100) {
      profiles.push('Symmetrical');
    }
    
    return profiles.length > 0 ? profiles[0] : null;
  }
}));

export default useComparisonStore;